import {
  CollectionReference,
  Query,
  QueryDocumentSnapshot,
} from "@atgof-firebase/firebase";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ListRenderItem, ListRenderItemInfo } from "@shopify/flash-list";
import { View } from "react-native";
import { FlashList } from "./FlashList";
import { Searchbar, SegmentedButtons } from "react-native-paper";
import { LanguageContext } from "../common/language";
import { matchesSearch, tokeniseQuery } from "../common/search";
import { PhraseKey } from "@atgof-common/phrases";
import { retrieveItem, storeItem } from "../data/prefs";

export type CollectionSpec<T> = {
  collection: CollectionReference;
  addConstraints?: (q: Query) => Query | undefined;
  sortComparison?: ((a: T, b: T) => number) | undefined;
  process: (items: QueryDocumentSnapshot[]) => T[];
  onCountChanged?: (count: number) => void;
  includeMetadata?: boolean;
};

export type CollectionListSpec<T, H> = CollectionSpec<T> & {
  renderItem: ListRenderItem<T>;
  searchTokens: (item: T) => (string | undefined)[];
  renderHeader?: (headerData?: H) => React.ReactElement;
  headerData?: H;
};

export type CollectionListGroups<T> = {
  selection: { default?: string; persistWithKey: string };
  items: { key: string; label: PhraseKey; predicate: (item: T) => boolean }[];
};

export type CollectionListProps<H> = {
  collections: CollectionListSpec<any, any>[] | undefined;
  headerItems?: H[];
  renderHeaderItem: ListRenderItem<H>;
  estimatedItemSize: number;
  groups?: CollectionListGroups<any>;
};

type Entry = { collIdx: number; obj: any; header?: boolean };

function sortItems<T>(
  compare: ((a: T, b: T) => number) | undefined,
  items: T[] | undefined,
) {
  if (!compare) return items;
  return items?.sort(compare);
}

function onCollectionItems<T>(
  collSpec: CollectionSpec<T> | undefined,
  onItems: (items: T[] | undefined) => void,
) {
  if (!collSpec) return;
  const {
    collection,
    addConstraints,
    sortComparison,
    process,
    onCountChanged,
    includeMetadata,
  } = collSpec;
  const q = addConstraints ? addConstraints(collection) : collection;
  return q?.onSnapshot(
    { includeMetadataChanges: includeMetadata },
    (querySnapshot) => {
      onItems(
        querySnapshot
          ? sortItems(sortComparison, process(querySnapshot.docs))
          : undefined,
      );
      onCountChanged && onCountChanged(querySnapshot.size);
    },
  );
}

export type FilterListUIData = {
  groupButtons: { key: string; label: string }[] | undefined;
  selectedGroup: string | undefined;
  setSelectedGroup: (v: string) => void;
  searchbarPlaceholder: string;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
};

export function renderListFilterComponent({
  groupButtons,
  selectedGroup,
  setSelectedGroup,
  searchbarPlaceholder,
  setSearchQuery,
  searchQuery,
}: FilterListUIData) {
  return (
    <View>
      {groupButtons ? (
        <SegmentedButtons
          value={selectedGroup ?? ""}
          onValueChange={setSelectedGroup}
          buttons={groupButtons.map(({ key, label }) => ({
            value: key,
            label,
          }))}
        />
      ) : null}
      <Searchbar
        style={{ marginVertical: 4 }}
        placeholder={searchbarPlaceholder}
        onChangeText={setSearchQuery}
        value={searchQuery}
      />
    </View>
  );
}

export function useFilterListHeader<T>(
  groups: CollectionListGroups<T> | undefined,
) {
  const { ph } = React.useContext(LanguageContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup_] = useState<string>();
  const groupSelectionPersistK = groups?.selection.persistWithKey;
  const setSelectedGroup = (v: string) => {
    setSelectedGroup_(v);
    if (groupSelectionPersistK) storeItem(groupSelectionPersistK, v);
  };
  useEffect(() => {
    let cancelled = false;
    if (selectedGroup) return;
    const set = async () => {
      const v = groupSelectionPersistK
        ? await retrieveItem(groupSelectionPersistK)
        : undefined;
      if (!cancelled) setSelectedGroup_(v ?? groups?.selection.default);
    };
    set();
    return () => {
      cancelled = true;
    };
  }, [selectedGroup, groups]);
  const groupFilter = useMemo(
    () => groups?.items.find(({ key }) => key === selectedGroup)?.predicate,
    [groups, selectedGroup],
  );
  const queryTokens = useMemo(() => tokeniseQuery(searchQuery), [searchQuery]);
  const filter = useCallback<
    (
      item: T,
      searchTokens: CollectionListSpec<T, any>["searchTokens"],
    ) => boolean
  >(
    (item, searchTokens) =>
      (!groupFilter || groupFilter(item)) &&
      (!queryTokens?.length || matchesSearch(queryTokens, searchTokens(item))),
    [groupFilter, queryTokens],
  );
  const filterUIData = useMemo<FilterListUIData>(
    () => ({
      groupButtons: groups?.items.map(({ key, label }) => ({
        key,
        label: ph(label) as string,
      })),
      selectedGroup,
      setSelectedGroup,
      searchbarPlaceholder: ph("search") as string,
      searchQuery,
      setSearchQuery,
    }),
    [groups, ph, selectedGroup, searchQuery],
  );
  return { filter, filterUIData };
}

export function useCollectionItems<T>(collSpec: CollectionSpec<T> | undefined) {
  const [items, setItems] = React.useState<T[] | undefined>();
  React.useEffect(() => onCollectionItems(collSpec, setItems), [collSpec]);
  return items;
}

type ListDataItem<H> =
  | Entry
  | { headerIdx: number; headerItem: H }
  | { filterUIData: FilterListUIData };

export default function CollectionList<H>({
  collections,
  headerItems,
  renderHeaderItem,
  estimatedItemSize,
  groups,
}: CollectionListProps<H>) {
  const [items, setItems] = React.useState<Record<number, any[] | undefined>>();
  React.useEffect(() => {
    setItems(undefined);
    const unsubs = collections?.map((collSpec, collIdx) =>
      onCollectionItems(collSpec, (collItems) =>
        setItems((items) => ({ ...items, [collIdx]: collItems })),
      ),
    );
    return () => {
      for (const unsub of unsubs || []) if (unsub) unsub();
    };
  }, [collections]);
  const { filter, filterUIData } = useFilterListHeader(groups);
  const data = React.useMemo<ListDataItem<H>[] | undefined>(() => {
    return [
      ...(headerItems ?? []).map((headerItem, headerIdx) => ({
        headerIdx,
        headerItem,
      })),
      ...(collections
        ? [
            { filterUIData },
            ...collections?.flatMap(
              ({ renderHeader, headerData, searchTokens }, collIdx) => {
                return [
                  ...(renderHeader
                    ? [{ collIdx, obj: headerData, header: true }]
                    : []),
                  ...((items && items[collIdx]) ?? [])
                    .filter((item) => filter(item, searchTokens))
                    .map((obj) => ({ collIdx, obj })),
                ];
              },
            ),
          ]
        : []),
    ];
  }, [headerItems, items, filter, filterUIData]);

  const renderItem = React.useCallback(
    (info: ListRenderItemInfo<ListDataItem<H>>) => {
      const { item } = info;
      if ("headerItem" in item) {
        return renderHeaderItem({ ...info, item: item.headerItem });
      }
      if ("filterUIData" in item) {
        return renderListFilterComponent(item.filterUIData);
      }
      if (!collections || item.collIdx >= collections.length) return null;
      if (item.header) {
        const { renderHeader } = collections[item.collIdx];
        if (renderHeader) return renderHeader(item.obj);
      } else {
        const { renderItem } = collections[item.collIdx];
        return renderItem({ ...info, item: item.obj });
      }
      return null;
    },
    [collections],
  );

  const getItemType = React.useCallback(
    (item: ListDataItem<H>) => {
      if (typeof item === "number") return `header-${item}`;
      else if ("filterUIData" in item) return "filter";
      else if ("headerIdx" in item) return `headerItem-${item.headerIdx}`;
      return `item-${item.collIdx}`;
    },
    [collections],
  );
  return (
    <FlashList
      data={data}
      renderItem={renderItem}
      getItemType={getItemType}
      estimatedItemSize={estimatedItemSize}
    />
  );
}
